/**
 * Shared autoplay policy for every carousel on the storefront, so rails and slide
 * carousels advance on the same cadence and honour the same pause rules.
 */

/** Scrolling rails: one card step per tick. */
export const CAROUSEL_AUTOPLAY_INTERVAL_MS = 4500;

/** Slide carousels replace the whole frame, so they dwell a little longer. */
export const CAROUSEL_SLIDE_AUTOPLAY_INTERVAL_MS = 5000;

/** Let the page settle (images, fonts, layout) before the first automatic move. */
export const CAROUSEL_AUTOPLAY_START_DELAY_MS = 800;

/** How long autoplay stays paused after the visitor scrolls or swipes a rail. */
export const CAROUSEL_AUTOPLAY_INTERACTION_PAUSE_MS = 6000;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function reducedMotionQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY);
}

export function prefersReducedMotion(): boolean {
  return reducedMotionQuery()?.matches ?? false;
}

/**
 * Reacts to the visitor toggling the OS motion setting while the page is open.
 * Returns a teardown function.
 */
export function onReducedMotionChange(listener: (reduced: boolean) => void): () => void {
  const query = reducedMotionQuery();
  if (!query) {
    return () => undefined;
  }

  const handler = (event: MediaQueryListEvent): void => listener(event.matches);

  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }

  // Safari < 14 only exposes the deprecated listener API.
  query.addListener(handler);
  return () => query.removeListener(handler);
}
